import { MqttCombination } from '@/shared/DefaultValues'
import { SelectItem } from '@/shared/SelectItem'
import { AlarmItem, VariableItem } from '@/template/interface'
import { getFormatMessage } from '@/utils/utils'
import {
  Button,
  Col,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table
} from 'antd'
import React, { useContext, useRef, useState } from 'react'
import { SelectionType, TopicType, Variable } from './Variable'
import styles from './EditableTable.css'
import {
  mqttObjectTypes,
  mqttValueTypeItems,
  mqttVariableDefaultType,
  mqttVariableType
} from '@/shared/SelectData'
import {
  DeleteOutlined,
  LinkOutlined,
  PlusOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import { Guid } from '@/utils/guid'

const EditableContext = React.createContext<FormInstance<any> | null>(null)
interface EditableRowProps {
  index: number
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm()
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  )
}

interface EditableCellProps {
  title: React.ReactNode
  editable: boolean
  type: string
  items?: SelectItem[]
  children: React.ReactNode
  dataIndex: keyof DataType
  record: DataType
  handleSave: (record: DataType) => void
}

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  type,
  items,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const inputRef = useRef<Input>(null)
  const form = useContext(EditableContext)!

  const save = async () => {
    try {
      const values = await form.validateFields()
      handleSave({ ...record, ...values })
    } catch (errInfo) {
      console.log('Save failed:', errInfo)
    }
  }

  let childNode = children
  let disabled =
    title == '默认值' && mqttObjectTypes.includes(record.valueType || '')

  if (editable) {
    if (type == 'select') {
      childNode = (
        <Form.Item style={{ margin: 0 }} name={dataIndex}>
          <Select ref={inputRef} onChange={save}>
            {items?.map((x) => (
              <Select.Option key={Guid.raw()} value={x.value}>
                {x.text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )
    } else {
      childNode = (
        <div className={styles['editable-cell']}>
          {children}
          <Form.Item style={{ margin: 0 }} name={dataIndex}>
            <Input
              disabled={disabled}
              ref={inputRef}
              onPressEnter={save}
              onBlur={save}
            />
          </Form.Item>
        </div>
      )
    }

    form.setFieldsValue({ [dataIndex]: record[dataIndex] })
  }

  return <td {...restProps}>{childNode}</td>
}

export interface DataType {
  key: React.Key
  valueType?: string
  anonymousName?: string
  defaultValue?: string
  valueTypeItems?: SelectItem[]
  combinationId?: string | null
  variableId?: string | null
  variableType?: number
  variableName?: string
  relateName?: string | null
  children?: DataType[] | null
}

export interface EditableTableProps {
  data: DataType[]
  topicType: TopicType
  selectFunc?: string
  alarms?: AlarmItem[]
  variables?: VariableItem[]
  combinations?: MqttCombination[]
  enableCache?: boolean
  onDataChange?: (data: DataType[], combination?: MqttCombination) => void
}

export const EditableTable: React.FC<EditableTableProps> = (
  props: EditableTableProps
) => {
  const formatMessage = getFormatMessage()
  const [data, setData] = useState<DataType[]>(props.data)
  const [variableVisible, setVariableVisible] = useState<boolean>(false)
  const [selectionType, setSelectionType] = useState<SelectionType>('checkbox')
  const [editItem, setEditItem] = useState<DataType>()
  const [selectVariables, setSelectVariables] = useState<VariableItem[]>()
  const [selectCombination, setSelectCombination] = useState<MqttCombination>()
  const labelCol = {
    xs: { span: 24 },
    sm: { span: 4 }
  }
  const wrapperCol = {
    xs: { span: 24 },
    sm: { span: 20 }
  }
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell
    }
  }
  const columns = [
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.editableTable.anonymousName'
      }),
      dataIndex: 'anonymousName',
      editable: true,
      render: (text: any, record: any, index: any) => ''
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.editableTable.valueType'
      }),
      dataIndex: 'valueType',
      editable: true,
      type: 'select'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.editableTable.variableName'
      }),
      dataIndex: 'variableName'
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.editableTable.defaultValue'
      }),
      dataIndex: 'defaultValue',
      editable: true,
      render: (text: any, record: any, index: any) => ''
    },
    {
      title: formatMessage({
        id: 'app.template.transfer.connection.editableTable.operation'
      }),
      dataIndex: 'operation',
      render: (text: string, record: any, index: number) => {
        const isObject = mqttObjectTypes.includes(record.valueType || '')
        return (
          <Space>
            <Button
              type='link'
              icon={<DeleteOutlined />}
              onClick={() => {
                handleDelete(record)
              }}
            ></Button>
            {isObject && (
              <Button
                type='link'
                icon={<PlusOutlined />}
                onClick={() => {
                  handleAdd(record)
                }}
              ></Button>
            )}
            {isObject && (
              <Button
                onClick={() => handleImport(record)}
                type='link'
                icon={<UnorderedListOutlined />}
              ></Button>
            )}
            {!isObject && (
              <Button
                onClick={() => handleLink(record)}
                type='link'
                icon={<LinkOutlined />}
              ></Button>
            )}
          </Space>
        )
      }
    }
  ].map((col) => {
    if (!col.editable) {
      return col
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        editable: col.editable,
        type: col.type,
        items: record.valueTypeItems,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave
      })
    }
  })

  const deleteDataItem = (data: DataType[], key: React.Key) => {
    for (let i = 0; i < data.length; i++) {
      const item = data[i]
      if (item.key == key) {
        data.splice(i, 1)
      } else {
        if (item.children && item.children.length > 0) {
          deleteDataItem(item.children, key)
        }
      }
    }
  }

  const updateDataItem = (data: DataType[], dt: DataType) => {
    for (let i = 0; i < data.length; i++) {
      const item = data[i]
      if (item.key == dt.key) {
        data[i] = dt
      } else {
        if (item.children && item.children.length > 0) {
          updateDataItem(item.children, dt)
        }
      }
    }
  }

  const handleDelete = (record: DataType) => {
    const newData = [...data]
    deleteDataItem(newData, record.key || '')
    setData(newData)
    if (props.onDataChange) {
      props.onDataChange(newData)
    }
  }

  const handleAdd = (parent?: DataType) => {
    let defaultCombination = props.combinations?.find((x) => x.name === 'KV')
    const item: DataType = {
      key: Guid.raw(),
      valueType: 'integer',
      anonymousName: '',
      defaultValue: '',
      variableId: null,
      valueTypeItems: mqttValueTypeItems,
      combinationId: defaultCombination ? defaultCombination.id : null,
      variableName: '',
      relateName: null,
      children: null
    }

    const newData = [...data]
    if (parent) {
      if (!parent.children) parent.children = []
      parent.children.push(item)
      setData(newData)
    } else {
      newData.push(item)
      setData(newData)
    }
  }

  const handleSave = (row: DataType) => {
    const newData = [...data]
    updateDataItem(newData, row)
    setData(newData)
    if (props.onDataChange) {
      props.onDataChange(newData)
    }
  }

  const updateRowData = (
    items: VariableItem[],
    combination: MqttCombination
  ) => {
    let newData = [...data]
    const dataItems = items.map((x) => {
      const dataTypes: SelectItem[] = x.enabledIndex
        ? mqttVariableType[0]
        : mqttVariableType[x.dataType!]
      let defaultType = dataTypes[0].value
      // 启用按位索引时，默认MQTT数据类型为bool
      if (x.enabledIndex) {
        defaultType = mqttVariableDefaultType[0].value
      } else if (mqttVariableDefaultType[x.dataType!]) {
        defaultType = mqttVariableDefaultType[x.dataType!].value
      }
      return {
        key: Guid.raw(),
        variableName: x.name,
        valueTypeItems: dataTypes,
        valueType: defaultType,
        anonymousName: x.name || '',
        combinationId: combination.id,
        variableId: x.id,
        variableType: x.dataType,
        relateName: x.name
      } as DataType
    })
    if (editItem) {
      if (selectionType == 'checkbox') {
        if (!editItem.children) editItem.children = []
        editItem.children = editItem.children.concat(dataItems)
      } else if (dataItems.length == 1) {
        editItem.variableName = dataItems[0].variableName
        editItem.valueTypeItems = dataItems[0].valueTypeItems
        editItem.valueType = dataItems[0].valueType
        editItem.anonymousName = dataItems[0].anonymousName
        editItem.combinationId = dataItems[0].combinationId
        editItem.variableId = dataItems[0].variableId
        editItem.variableType = dataItems[0].variableType
        editItem.relateName = dataItems[0].relateName
      }
    } else {
      newData = data.concat(dataItems)
    }
    setData(newData)
    setEditItem(undefined)
    setSelectVariables(undefined)
    setSelectCombination(undefined)
    if (props.onDataChange) {
      props.onDataChange(newData, combination)
    }
  }

  const handleVariableModalCancel = () => {
    setVariableVisible(false)
  }

  const handleVariableModalOk = () => {
    setVariableVisible(false)
    if (selectVariables && selectCombination) {
      updateRowData(selectVariables, selectCombination)
    }
  }

  const handleVariableChange = (
    items: VariableItem[],
    combination: MqttCombination
  ) => {
    setSelectVariables(items)
    setSelectCombination(combination)
  }

  const handleImport = (editItem?: DataType) => {
    setVariableVisible(true)
    setSelectionType('checkbox')
    if (editItem) {
      setEditItem(editItem)
    }
  }

  const handleLink = (editItem: DataType) => {
    setVariableVisible(true)
    setSelectionType('radio')
    setEditItem(editItem)
  }

  return (
    <div>
      <Row gutter={24}>
        <Col span={16}>
          <Form.Item
            label={formatMessage({
              id: 'app.template.transfer.connection.editableTable.topic'
            })}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
            name={[props.topicType, 'topic']}
            rules={[
              {
                required: true,
                transform: (value) => value?.trim()
              }
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Space style={{ marginBottom: 16 }}>
            <Button onClick={() => handleAdd()} type='primary'>
              {formatMessage({
                id: 'app.template.transfer.connection.editableTable.create'
              })}
            </Button>
            <Button onClick={() => handleImport()} type='primary'>
              {formatMessage({
                id: 'app.template.transfer.connection.editableTable.import'
              })}
            </Button>
          </Space>
        </Col>
      </Row>
      {props.enableCache && (
        <Row>
          <Col span={16}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.transfer.connection.editableTable.cacheTopic'
              })}
              labelCol={labelCol}
              wrapperCol={wrapperCol}
              name={[props.topicType, 'cacheTopic']}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}></Col>
        </Row>
      )}

      <Table
        components={components}
        rowClassName={styles['editable-row']}
        bordered
        dataSource={data}
        columns={columns}
      />
      <Modal
        visible={variableVisible}
        title={formatMessage({
          id: 'app.template.transfer.connection.variable.contact'
        })}
        onCancel={handleVariableModalCancel}
        onOk={handleVariableModalOk}
        width={700}
        destroyOnClose={true}
        maskClosable={false}
        okText={formatMessage({ id: 'app.modal.ok' })}
        cancelText={formatMessage({ id: 'app.modal.cancel' })}
      >
        <Variable
          selectionType={selectionType}
          selectFunc={props.selectFunc}
          topicType={props.topicType}
          variables={props.variables}
          alarms={props.alarms}
          combinations={props.combinations}
          onChange={handleVariableChange}
        />
      </Modal>
    </div>
  )
}
